import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

// ==========================================
// 1. Shifts (Legacy & Enhanced)
// ==========================================
export function useShifts() {
  const APIURL = "/hr/shifts";
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate };
}

export async function addNewShift(name, startTime, endTime) {
  try {
    const response = await ApiClient.post("/hr/shifts/add", {
      name,
      start_time: startTime,
      end_time: endTime,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to add shift";
  }
}

export async function deleteShift(id) {
  try {
    const response = await ApiClient.delete(`/hr/shifts/delete/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete shift";
  }
}

// ==========================================
// 2. Shift Logs (Legacy)
// ==========================================
export function useShiftLogs(startDate, endDate) {
  const APIURL = startDate && endDate ? `/hr/shift-logs?startDate=${startDate}&endDate=${endDate}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate };
}

export async function addNewShiftLog(username, shiftId, date) {
  try {
    const response = await ApiClient.post("/hr/shift-logs/add", {
      username,
      shift_id: shiftId,
      date,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to log shift";
  }
}

export async function deleteShiftLog(id) {
  try {
    const response = await ApiClient.delete(`/hr/shift-logs/delete/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete shift log";
  }
}

// ==========================================
// 3. Transactions (Incentives, Fines, Advances - Legacy)
// ==========================================
export function useStaffTransactions(startDate, endDate) {
  const APIURL = startDate && endDate ? `/hr/transactions?startDate=${startDate}&endDate=${endDate}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate };
}

export async function addNewStaffTransaction(username, type, amount, reason, date) {
  try {
    const response = await ApiClient.post("/hr/transactions/add", {
      username,
      type,
      amount: parseFloat(amount),
      reason,
      date,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to record transaction";
  }
}

export async function deleteStaffTransaction(id) {
  try {
    const response = await ApiClient.delete(`/hr/transactions/delete/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete transaction";
  }
}

export function useStaffPayrollDetails(startDate, endDate) {
  const APIURL = startDate && endDate ? `/hr/payroll-details?startDate=${startDate}&endDate=${endDate}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate };
}

// ==========================================
// 4. Departments & Designations
// ==========================================
export function useDepartments() {
  const { data, error, isLoading, mutate } = useSWR("/hr/departments", fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createDepartment(name, code, parentDepartmentId = null) {
  try {
    const response = await ApiClient.post("/hr/departments", {
      name,
      code,
      parent_department_id: parentDepartmentId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create department";
  }
}

export async function deleteDepartment(id) {
  try {
    const response = await ApiClient.delete(`/hr/departments/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete department";
  }
}

export function useDesignations() {
  const { data, error, isLoading, mutate } = useSWR("/hr/designations", fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createDesignation(name, code, departmentId = null) {
  try {
    const response = await ApiClient.post("/hr/designations", {
      name,
      code,
      department_id: departmentId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create designation";
  }
}

export async function deleteDesignation(id) {
  try {
    const response = await ApiClient.delete(`/hr/designations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete designation";
  }
}

// ==========================================
// 5. Employee Master
// ==========================================
export function useEmployees(filters = {}) {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  if (filters.department_id) query.set("department_id", filters.department_id);
  if (filters.search) query.set("search", filters.search);

  const url = `/hr/employees${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export function useEmployeeById(id) {
  const { data, error, isLoading, mutate } = useSWR(id ? `/hr/employees/${id}` : null, fetcher);
  return { data, error, isLoading, mutate };
}

export async function createEmployee(employeeData) {
  try {
    const response = await ApiClient.post("/hr/employees", employeeData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create employee";
  }
}

export async function updateEmployee(id, employeeData) {
  try {
    const response = await ApiClient.put(`/hr/employees/${id}`, employeeData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update employee";
  }
}

export async function deleteEmployee(id) {
  try {
    const response = await ApiClient.delete(`/hr/employees/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete employee";
  }
}

export async function createLoginForEmployee(id, { username, password, role_id }) {
  try {
    const response = await ApiClient.post(`/hr/employees/${id}/create-login`, {
      username,
      password,
      role_id,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create login account";
  }
}

export async function unlinkLoginForEmployee(id) {
  try {
    const response = await ApiClient.post(`/hr/employees/${id}/unlink-login`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to unlink login";
  }
}

// ==========================================
// 6. Holiday Calendars & Holidays
// ==========================================
export function useHolidayCalendars() {
  const { data, error, isLoading, mutate } = useSWR("/hr/holiday-calendars", fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createHolidayCalendar(data) {
  try {
    const response = await ApiClient.post("/hr/holiday-calendars", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create holiday calendar";
  }
}

export async function deleteHolidayCalendar(id) {
  try {
    const response = await ApiClient.delete(`/hr/holiday-calendars/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete holiday calendar";
  }
}

export function useHolidays(calendarId, year) {
  const query = new URLSearchParams();
  if (calendarId) query.set("calendar_id", calendarId);
  if (year) query.set("year", year);
  const url = `/hr/holidays${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function addHoliday(holidayData) {
  try {
    const response = await ApiClient.post("/hr/holidays", holidayData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to add holiday";
  }
}

export async function deleteHoliday(id) {
  try {
    const response = await ApiClient.delete(`/hr/holidays/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete holiday";
  }
}

// ==========================================
// 7. Attendance & Dashboard
// ==========================================
export function useAttendance(filters = {}) {
  const query = new URLSearchParams();
  if (filters.date) query.set("date", filters.date);
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.department_id) query.set("department_id", filters.department_id);
  if (filters.startDate) query.set("startDate", filters.startDate);
  if (filters.endDate) query.set("endDate", filters.endDate);

  const url = `/hr/attendance${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function manualAttendanceEntry(attendanceData) {
  try {
    const response = await ApiClient.post("/hr/attendance/manual", attendanceData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update attendance";
  }
}

export function useAttendanceDashboardStats(date) {
  const url = date ? `/hr/attendance/dashboard-stats?date=${date}` : "/hr/attendance/dashboard-stats";
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || {}, error, isLoading, mutate };
}

// ==========================================
// 8. Leave Management
// ==========================================
export function useLeaveTypes() {
  const { data, error, isLoading, mutate } = useSWR("/hr/leave-types", fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createLeaveType(data) {
  try {
    const response = await ApiClient.post("/hr/leave-types", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create leave type";
  }
}

export function useEmployeeLeaveBalances(employeeId, year) {
  const url = employeeId ? `/hr/leave-balances/${employeeId}${year ? `?year=${year}` : ""}` : null;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export function useLeaveApplications(filters = {}) {
  const query = new URLSearchParams();
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.status) query.set("status", filters.status);
  if (filters.year) query.set("year", filters.year);

  const url = `/hr/leave-applications${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function applyLeave(data) {
  try {
    const response = await ApiClient.post("/hr/leave-applications", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to apply for leave";
  }
}

export async function approveRejectLeave(id, status, reviewRemarks = "") {
  try {
    const response = await ApiClient.post(`/hr/leave-applications/${id}/review`, {
      status,
      review_remarks: reviewRemarks,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to process leave application";
  }
}

// ==========================================
// 9. Statutory Settings (Nepal SSF, Tax)
// ==========================================
export function useStatutorySettings() {
  const { data, error, isLoading, mutate } = useSWR("/hr/statutory-settings", fetcher);
  return { data, error, isLoading, mutate };
}

export async function updateStatutorySettings(settingsData) {
  try {
    const response = await ApiClient.put("/hr/statutory-settings", settingsData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update statutory settings";
  }
}

// ==========================================
// 10. Overtime Management
// ==========================================
export function useOvertimeRecords(filters = {}) {
  const query = new URLSearchParams();
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.status) query.set("status", filters.status);
  if (filters.startDate) query.set("startDate", filters.startDate);
  if (filters.endDate) query.set("endDate", filters.endDate);

  const url = `/hr/overtime-records${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createOvertimeRecord(data) {
  try {
    const response = await ApiClient.post("/hr/overtime-records", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to submit overtime record";
  }
}

export async function approveRejectOvertime(id, status, approvedHours, reviewRemarks = "") {
  try {
    const response = await ApiClient.post(`/hr/overtime-records/${id}/review`, {
      status,
      approved_hours: approvedHours,
      review_remarks: reviewRemarks,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to review overtime record";
  }
}

// ==========================================
// 11. Salary Advances & Loans
// ==========================================
export function useSalaryAdvances(filters = {}) {
  const query = new URLSearchParams();
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.status) query.set("status", filters.status);

  const url = `/hr/salary-advances${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createSalaryAdvance(data) {
  try {
    const response = await ApiClient.post("/hr/salary-advances", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to request salary advance";
  }
}

export async function disburseSalaryAdvance(id, paymentMode = "cash", referenceNo = "") {
  try {
    const response = await ApiClient.post(`/hr/salary-advances/${id}/disburse`, {
      payment_mode: paymentMode,
      reference_no: referenceNo,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to disburse salary advance";
  }
}

// ==========================================
// 12. Security Deposits (Strict Liability)
// ==========================================
export function useSecurityDeposits(filters = {}) {
  const query = new URLSearchParams();
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.status) query.set("status", filters.status);

  const url = `/hr/security-deposits${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function recordSecurityDepositReceipt(data) {
  try {
    const response = await ApiClient.post("/hr/security-deposits/receipt", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to record security deposit";
  }
}

export async function refundSecurityDeposit(id, paymentMode = "cash", referenceNo = "", remarks = "") {
  try {
    const response = await ApiClient.post(`/hr/security-deposits/${id}/refund`, {
      payment_mode: paymentMode,
      reference_no: referenceNo,
      remarks,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to refund security deposit";
  }
}

// ==========================================
// 13. Salary Holds Register
// ==========================================
export function useSalaryHolds(filters = {}) {
  const query = new URLSearchParams();
  if (filters.employee_id) query.set("employee_id", filters.employee_id);
  if (filters.status) query.set("status", filters.status);

  const url = `/hr/salary-holds${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createSalaryHold(data) {
  try {
    const response = await ApiClient.post("/hr/salary-holds", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to record salary hold";
  }
}

export async function releaseSalaryHold(id, releaseReason) {
  try {
    const response = await ApiClient.post(`/hr/salary-holds/${id}/release`, {
      release_reason: releaseReason,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to release salary hold";
  }
}

// ==========================================
// 14. Payroll Periods & Engine
// ==========================================
export function usePayrollPeriods() {
  const { data, error, isLoading, mutate } = useSWR("/hr/payroll-periods", fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function createPayrollPeriod(data) {
  try {
    const response = await ApiClient.post("/hr/payroll-periods", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create payroll period";
  }
}

export async function calculateMonthlyPayroll(periodId) {
  try {
    const response = await ApiClient.post(`/hr/payroll-periods/${periodId}/calculate`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to calculate payroll";
  }
}

export function usePayrollPeriodEmployees(periodId) {
  const url = periodId ? `/hr/payroll-periods/${periodId}/employees` : null;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function approvePayrollPeriod(periodId) {
  try {
    const response = await ApiClient.post(`/hr/payroll-periods/${periodId}/approve`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to approve payroll";
  }
}

export async function reopenPayrollPeriod(periodId, reopenReason) {
  try {
    const response = await ApiClient.post(`/hr/payroll-periods/${periodId}/reopen`, {
      reopen_reason: reopenReason,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to reopen payroll";
  }
}

// ==========================================
// 15. Salary Payments
// ==========================================
export function useSalaryPayments(filters = {}) {
  const query = new URLSearchParams();
  if (filters.period_id) query.set("period_id", filters.period_id);
  if (filters.employee_id) query.set("employee_id", filters.employee_id);

  const url = `/hr/salary-payments${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || [], error, isLoading, mutate };
}

export async function recordSalaryPayment(data) {
  try {
    const response = await ApiClient.post("/hr/salary-payments", data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to record salary payment";
  }
}

// ==========================================
// 16. Dashboard KPIs & Payslips
// ==========================================
export function usePayrollDashboardKPIs(month, year) {
  const query = new URLSearchParams();
  if (month) query.set("month", month);
  if (year) query.set("year", year);

  const url = `/hr/dashboard-kpis${query.toString() ? `?${query.toString()}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data: data || {}, error, isLoading, mutate };
}

export function useEmployeePayslipData(periodId, employeeId) {
  const url = periodId && employeeId ? `/hr/payslip/${periodId}/${employeeId}` : null;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);
  return { data, error, isLoading, mutate };
}
